/**
 * NavGrid — the traversability + cost view that all pathfinders consume. It
 * combines the static terrain move-cost with the dynamic road layer, so a single
 * abstraction feeds A*, flow fields and HPA*. Pure and deterministic.
 */
import type { GameMap } from '../map/gamemap.js';
import type { RoadNetwork } from '../roads/roadNetwork.js';
import { ROAD_COST_FACTOR } from '../roads/roadNetwork.js';

export class NavGrid {
  readonly width: number;
  readonly height: number;

  constructor(
    private readonly map: GameMap,
    private readonly roads: RoadNetwork,
  ) {
    this.width = map.width;
    this.height = map.height;
  }

  index(x: number, y: number): number {
    return y * this.width + x;
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  /** True if a unit may stand on / cross this tile. */
  passable(x: number, y: number): boolean {
    return this.inBounds(x, y) && Number.isFinite(this.map.moveCostAt(x, y));
  }

  /** Enter-cost of a tile (terrain cost, discounted by any road). Infinity if
   * impassable. */
  cost(x: number, y: number): number {
    const base = this.map.moveCostAt(x, y);
    if (!Number.isFinite(base)) return Number.POSITIVE_INFINITY;
    return this.roads.has(x, y) ? base * ROAD_COST_FACTOR : base;
  }
}
